import { Easing, interpolate } from 'remotion';

// Constants
import {
  ANIMATION_DURATIONS,
  ENTRANCE_ANIMATION,
  FLOAT_ANIMATION,
  SHEEN_ANIMATION,
} from './constants';
import { CLAMP_BOTH } from '@/constants';

// Exponential easing for entrance
const getEntranceProgress = (
  frame: number,
  delay: number,
  duration: number,
): number =>
  Easing.out(Easing.exp)(
    interpolate(frame, [delay, delay + duration], [0, 1], CLAMP_BOTH),
  );

// Combine entrance + float animations with rotation effects
export const getEntranceAndFloatAnimation = (
  frame: number,
  fps: number,
  animationDelay: number,
  floatDuration: number,
  floatDirection: 'up' | 'down',
  entranceDirection: 'fromTop' | 'fromBottom',
): { translateY: number; rotateX: number; opacity: number } => {
  const easedProgress = getEntranceProgress(
    frame,
    animationDelay,
    ANIMATION_DURATIONS.entrance,
  );

  // Entrance: slide in and fade
  const entranceTranslateY = interpolate(
    easedProgress,
    [0, 1],
    ENTRANCE_ANIMATION.translateY[entranceDirection],
  );
  const opacity = interpolate(
    easedProgress,
    ENTRANCE_ANIMATION.opacitySteps,
    ENTRANCE_ANIMATION.opacity,
  );

  // Float: sine wave oscillation
  const floatFrames = floatDuration * fps;
  const floatProgress =
    ((frame - animationDelay - ANIMATION_DURATIONS.entrance) % floatFrames) /
    floatFrames;
  const floatOffset =
    Math.sin(floatProgress * Math.PI * 2) *
    FLOAT_ANIMATION.amplitude *
    (floatDirection === 'up' ? 1 : -1);

  // Rotation: entrance plus float
  const rotateX = interpolate(
    easedProgress,
    [0, 1],
    ENTRANCE_ANIMATION.rotateX[entranceDirection],
  );
  const floatRotateX =
    Math.sin(floatProgress * Math.PI * 2) * FLOAT_ANIMATION.rotateXAmplitude;

  // Combined transforms
  return {
    translateY: entranceTranslateY + floatOffset,
    rotateX: rotateX + floatRotateX,
    opacity,
  };
};

// Calculate sheen gradient position
export const calculateSheenPosition = (
  frame: number,
  fps: number,
  delay: number,
  entranceDuration: number,
): number => {
  const sheenCycle = SHEEN_ANIMATION.cycleDuration * fps;
  // Progress within current cycle (0 to 1)
  const sheenProgress =
    ((frame - delay - entranceDuration) % sheenCycle) / sheenCycle;

  // Map progress to sheen position
  return interpolate(
    sheenProgress,
    SHEEN_ANIMATION.positionSteps,
    SHEEN_ANIMATION.positionValues,
  );
};
